import {DashboardCard} from '../dashboard-card/DashboardCard'
import {useTranslation} from '@/translations/i18n'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Badge} from '@/components/ui/badge/Badge'
import {useGetDashboardOps} from '../../query/useGetDashboardOps'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {DashboardSearchParams} from '../../types'
import {useMemo} from 'react'
import {StyledDeviationGrid} from './style'
import {DashboardSimpleCardSkeleton} from '../dashboard-simple-card-skeleton/DashboardSimpleCardSkeleton'
import {formatNumber, formatPercentage} from '@/utilities/helpers'

export const DashboardDeviationCard = () => {
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(DashboardSearchParams)
    const {data, isLoading} = useGetDashboardOps({fields: ['graph'], ...searchParams})

    const toBePerformed = useMemo(() => {
        if (!data?.accessCounters?.planned || !data?.accessCounters?.completed) {
            return 0
        }
        const total = data.accessCounters.planned - data.accessCounters.completed
        return total > 0 ? total : 0
    }, [data])

    const deviation = useMemo(() => {
        return toBePerformed + (data?.accessCounters?.absences ?? 0)
    }, [data])

    const percentage = useMemo(() => {
        if (!data?.accessCounters?.completed || !data?.accessCounters?.planned) {
            return 0
        }
        return Math.abs(
            ((data?.accessCounters?.completed - data?.accessCounters?.planned) / data?.accessCounters?.planned) * 100
        )
    }, [toBePerformed, deviation])

    if (isLoading) {
        return <DashboardSimpleCardSkeleton title={t('dashboard:deviation')} />
    }

    return (
        <DashboardCard title={t('dashboard:deviation')}>
            <Flexbox direction="column" gap={4} fullWidth>
                <StyledDeviationGrid>
                    <Text palette="neutral" shade="900" size="displaySm" weight={700}>
                        {formatNumber(deviation)}
                    </Text>

                    <Flexbox align="center" gap={1}>
                        <Badge variant={percentage > 50 ? 'danger' : 'success'} size="sm">
                            {formatPercentage(percentage / 100)}
                        </Badge>
                        <Text palette="neutral" shade="600" size="textSm" weight={400}>
                            {t('dashboard:difference')}
                        </Text>
                    </Flexbox>
                </StyledDeviationGrid>
                <ul>
                    <li>
                        <Flexbox align="center" gap={1}>
                            <Text palette="neutral" shade="900" size="textSm" weight={600}>
                                {formatNumber(toBePerformed)}
                            </Text>
                            <Text palette="neutral" shade="600" size="textSm" weight={400}>
                                {t('dashboard:to_be_performed')}
                            </Text>
                        </Flexbox>
                    </li>
                    <li>
                        <Flexbox align="center" gap={1}>
                            <Text palette="neutral" shade="900" size="textSm" weight={600}>
                                {formatNumber(data?.accessCounters?.absences ?? 0)}
                            </Text>
                            <Text palette="neutral" shade="600" size="textSm" weight={400}>
                                {t('dashboard:absences')}
                            </Text>
                        </Flexbox>
                    </li>
                </ul>
            </Flexbox>
        </DashboardCard>
    )
}
